import { useShowsAiCreditBalance } from '@euler/functions/entitlements/useShowsAiCreditBalance';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import React, { memo } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  SlideInDown,
  SlideInRight,
  SlideOutDown,
  SlideOutRight,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useAIAssistantActions,
  useAIAssistantState,
  useAIAssistantVisible,
} from '../AIAssistantProvider';
import { EmptyState } from './EmptyState';
import { InputBar } from './InputBar';
import { MessageList } from './MessageList';
import { DragHandle, PanelHeader } from './PanelHeader';
import { SuggestionBar } from './SuggestionBar';
import { AiCreditNotice } from './ai-credits/AiCreditNotice';
import { useAiCreditBalance } from './functions/useAiCreditBalance';
import { useStyles } from './styles';

const DESKTOP_PANEL_WIDTH = 420;
const DESKTOP_PANEL_RIGHT = 16;
const DESKTOP_PANEL_GAP = 12;

export const AIAssistantOverlay = memo(function AIAssistantOverlay() {
  const isVisible = useAIAssistantVisible();
  if (!isVisible) return null;
  return <OverlayContent />;
});

const OverlayContent = memo(function OverlayContent() {
  const { height: windowHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const { rpx } = useResponsiveContext();
  const isMobile = useIsMobileLayout();
  const state = useAIAssistantState();
  const actions = useAIAssistantActions();
  const styles = useStyles();
  const credit = useAiCreditBalance();
  const showsBalance = useShowsAiCreditBalance();
  const header = (
    <>
      <PanelHeader
        creditBalance={showsBalance ? credit?.balance ?? null : null}
      />
      <AiCreditNotice alertState={credit?.alertState ?? null} />
    </>
  );

  if (isMobile) {
    const panelHeight = windowHeight * 0.88;
    return (
      <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
        <Animated.View
          entering={FadeIn.duration(250)}
          exiting={FadeOut.duration(200)}
          style={[StyleSheet.absoluteFill, styles.dimmer]}
        >
          <TouchableOpacity
            style={StyleSheet.absoluteFill}
            activeOpacity={1}
            onPress={actions.close}
          />
        </Animated.View>

        <Animated.View
          entering={SlideInDown.springify().damping(22).stiffness(200)}
          exiting={SlideOutDown.duration(250)}
          style={[
            styles.panel,
            { height: panelHeight, paddingBottom: insets.bottom },
          ]}
        >
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={{ flex: 1 }}
            keyboardVerticalOffset={windowHeight - panelHeight}
          >
            <DragHandle />
            {header}
            <View style={{ flex: 1 }}>
              {state.messages.length === 0 && !state.isLoading ? (
                <EmptyState />
              ) : (
                <MessageList />
              )}
            </View>
            {state.messages.length > 0 && <SuggestionBar />}
            <InputBar />
          </KeyboardAvoidingView>
        </Animated.View>
      </View>
    );
  }

  const buttonSize = rpx(52);
  const buttonBottom = insets.bottom + rpx(80);
  const panelBottom = buttonBottom + buttonSize + DESKTOP_PANEL_GAP;
  const panelHeight = windowHeight * 0.6;

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      <Animated.View
        entering={SlideInRight.springify().damping(22).stiffness(200)}
        exiting={SlideOutRight.duration(200)}
        style={[
          styles.desktopPanel,
          {
            width: DESKTOP_PANEL_WIDTH,
            height: panelHeight,
            right: DESKTOP_PANEL_RIGHT,
            bottom: panelBottom,
          },
        ]}
      >
        {header}
        <View style={{ flex: 1 }}>
          {state.messages.length === 0 && !state.isLoading ? (
            <EmptyState />
          ) : (
            <MessageList />
          )}
        </View>
        {state.messages.length > 0 && <SuggestionBar />}
        <InputBar />
      </Animated.View>
    </View>
  );
});
